<template>
  <div
    class="troca-de-passes-sportv-container"
    :class="[`position-${posicaoEnquete}`, { 'fullscreen-mode': isFullScreen }]"
    :key="item.id"
  >
    <!-- Title Bar (separate from options) -->
    <div v-if="!hideTitle" class="title-bar-wrapper">
      <BaseTitulo3Opcoes class="title-bg-svg" preserveAspectRatio="none" />
      <div class="title-bar">
        <span class="title-text">{{ item.dsc_titulo_enquete }}</span>
      </div>
    </div>

    <!-- Main Content Area (options only) -->
    <div class="main-content">
      <BaseBaixo3Opcoes class="main-bg" preserveAspectRatio="none" />
      
      <!-- Enquete Image (left side) -->
      <div v-if="hasEnqueteImage" class="enquete-image-section">
        <img
          :src="item.url_imagem_enquete"
          :alt="item.dsc_titulo_enquete"
          class="enquete-image"
          @error="handleEnqueteImageError"
        />
      </div>

      <!-- Options Grid -->
      <div class="options-grid" :class="{ 'has-images': hasOptionsWithImages, 'has-enquete-image': hasEnqueteImage, 'align-start': hasEnqueteImage && hasMultipleOptions }">
        <div
          v-for="opcao in limitedOpcoes"
          :key="opcao.id"
          class="option-item"
        >
          <!-- Image (if available and not broken) -->
          <div v-if="opcao.url_imagem && !brokenImages[opcao.id]" class="option-image">
            <img
              :src="opcao.url_imagem"
              :alt="opcao.dsc_titulo"
              @error="handleImageError(opcao.id)"
            />
          </div>

          <!-- Right section: Label on top, percentage + bar below -->
          <div class="option-content">
            <!-- Label -->
            <div class="option-label" v-if="!hideOptionTitle">
              <span
                v-for="(word, index) in (opcao.dsc_titulo || '').split(/\s+/).filter(Boolean)"
                :key="index"
                style="display: block;color:white;"
              >
                {{ word }}
              </span>
            </div>

            <!-- Progress Bar + Percentage row -->
            <div v-if="item.flg_exibir_resultado" class="percentage-row mt-1">
              <span class="percentage-value">
                {{ animatedPercentages[opcao.id] || 0 }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseBaixo3Opcoes from '@/assets/tarjas/F1Sportv/BASE_BAIXO_3OPCOES.svg';
import BaseTitulo3Opcoes from '@/assets/tarjas/F1Sportv/BASE_TITULO_3OPCOES.svg';

export default {
  name: 'CardTarjaEnqueteF1Sportv',
  components: {
    BaseBaixo3Opcoes,
    BaseTitulo3Opcoes,
  },
  props: {
    item: Object,
    posicaoEnquete: String,
    isFullScreen: Boolean,
    hideTitle: Boolean,
    hideOptionTitle: Boolean,
  },
  data() {
    return {
      animatedPercentages: {},
      animationDuration: 1200,
      optionColors: {},
      brokenImages: {},
      brokenEnqueteImage: false,
      animationFrameIds: {},
    };
  },
  created() {
    for (const option of (this.item?.enquete_opcoes || []).slice(0, 8)) {
      if (option.dsc_cor_barra) {
        this.optionColors[option.id] = option.dsc_cor_barra;
      }
    }
  },
  computed: {
    limitedOpcoes() {
      return (this.item?.enquete_opcoes || []).slice(0, 8);
    },
    hasOptionsWithImages() {
      return this.limitedOpcoes.some(
        opcao => opcao.url_imagem && !this.brokenImages[opcao.id]
      );
    },
    hasEnqueteImage() {
      return this.item?.url_imagem_enquete && !this.brokenEnqueteImage;
    },
    hasMultipleOptions() {
      return this.limitedOpcoes.length > 3;
    },
  },
  watch: {
    'item.flg_exibir_resultado': {
      handler(newVal) {
        if (newVal) {
          this.limitedOpcoes.forEach((opcao) => {
            this.animateValue(opcao.id, 0, opcao.resultado || 0);
          });
        } else {
          this.animatedPercentages = {};
        }
      },
      immediate: true,
    },
    'item.enquete_opcoes': {
      handler(newOpcoes, oldOpcoes) {
        if (newOpcoes) {
          const limitedNewOpcoes = newOpcoes.slice(0, 8);
          const limitedOldOpcoes = oldOpcoes?.slice(0, 8);

          limitedNewOpcoes.forEach((opcao) => {
            const oldOpcao = limitedOldOpcoes?.find(o => o.id === opcao.id);
            if (oldOpcao && oldOpcao.url_imagem !== opcao.url_imagem) {
              this.$set(this.brokenImages, opcao.id, false);
            }
          });

          if (this.item.flg_exibir_resultado) {
            limitedNewOpcoes.forEach((opcao) => {
              const currentValue = this.animatedPercentages[opcao.id] || 0;
              if (currentValue !== opcao.resultado) {
                this.animateValue(opcao.id, currentValue, opcao.resultado || 0);
              }
              if (opcao.dsc_cor_barra) {
                this.$set(this.optionColors, opcao.id, opcao.dsc_cor_barra);
              } else {
                this.$delete(this.optionColors, opcao.id);
              }
            });
          }
        }
      },
      deep: true,
    },
    'item.url_imagem_enquete': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.brokenEnqueteImage = false;
        }
      },
    },
  },
  beforeDestroy() {
    Object.values(this.animationFrameIds).forEach(id => cancelAnimationFrame(id));
  },
  methods: {
    animateValue(key, fromValue, toValue) {
      if (this.animationFrameIds[key]) {
        cancelAnimationFrame(this.animationFrameIds[key]);
      }
      const startTime = Date.now();
      const difference = toValue - fromValue;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / this.animationDuration, 1);
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);

        this.$set(
          this.animatedPercentages,
          key,
          Math.round(fromValue + difference * easeOutCubic)
        );

        if (progress < 1) {
          this.animationFrameIds[key] = requestAnimationFrame(animate);
        } else {
          delete this.animationFrameIds[key];
        }
      };

      this.animationFrameIds[key] = requestAnimationFrame(animate);
    },
    getProgressBarStyle(opcao) {
      const percentage = this.animatedPercentages[opcao.id] || 0;
      const style = { width: percentage + '%' };
      if (this.optionColors[opcao.id]) {
        style.backgroundColor = this.optionColors[opcao.id];
      }
      return style;
    },
    handleImageError(opcaoId) {
      this.$set(this.brokenImages, opcaoId, true);
    },
    handleEnqueteImageError() {
      this.brokenEnqueteImage = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "RigidSquareExtrabold";
  src: url("@/assets/tarjas/F1Sportv/RigidSquareExtrabold.ttf") format("truetype");
}

@font-face {
  font-family: "RigidSquareExtrabold_Italic";
  src: url("@/assets/tarjas/F1Sportv/RigidSquareExtrabold_Italic.ttf") format("truetype");
}

@font-face {
  font-family: "GaleanaExtended-Regular";
  src: url("@/assets/tarjas/F1Sportv/GaleanaStandard-Bold.otf") format("opentype");
}

@font-face {
  font-family: "GaleanaCondensed-Italic";
  src: url("@/assets/tarjas/F1Sportv/GaleanaStandard-BoldIt.otf") format("opentype");
}

.troca-de-passes-sportv-container {
  --background-gradient: linear-gradient(to right, #521925, #ae1314);
  --title-bar-background-gradient: linear-gradient(to right, #d7491e, #340228);
  --title-text-color: white;
  --progress-bar-bg: #430327;
  --progress-bar-fill: #c03519;
  --option-text-color: white;
  --percentage-text-color: white;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: fit-content;
  min-width: 650px;
  height: 194px;
  margin-bottom: 85px;

  &.position-esquerda {
    justify-self: flex-start;
    margin-left: 50px;
  }

  &.position-centro {
    justify-self: center;
    margin-left: auto;
    margin-right: auto;
  }

  &.position-direita {
    justify-self: flex-end;
    margin-right: 50px;
  }

  &.fullscreen-mode {
    max-width: 1200px;

    .title-bar {
      padding: 14px 40px;

      .title-text {
        font-size: 32px;
      }
    }

    .options-grid {
      padding: 30px 40px;
    }

    .percentage-value {
      font-size: 42px;
    }

    .option-label {
      font-size: 28px;
    }

    .option-image img {
      width: 64px;
      height: 64px;
    }

    .progress-bar-container {
      width: 120px;
      height: 28px;
    }

    .enquete-image-section {
      padding: 15px 10px 15px 25px;

      .enquete-image {
        width: 120px;
        height: 120px;
      }
    }
  }
}

.main-content {
  overflow: hidden;
  position: relative;
  flex: 1;
  display: flex;
  min-width: 650px;
  flex-direction: row;
  align-items: center;

  .main-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }
}

.enquete-image-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 5px 10px 15px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;

  .enquete-image {
    width: 90px;
    height: 90px;
    object-fit: contain;
  }
}

.title-bar-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  .title-bg-svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }
}

.title-bar {
  padding: 0 30px;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  width: 100%;

  .title-text {
    display: block;
    color: var(--title-text-color);
    font-family: 'GaleanaExtended-Regular', sans-serif;
    font-size: 22px;
    text-align: center;
    position: relative;
    z-index: 1;
    margin-bottom: 2px;
  }
}

.options-grid {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  padding: 15px 40px 25px 40px;
  width: 100%;
  position: relative;
  z-index: 1;

  &.has-enquete-image {
    padding-left: 10px;
  }

  &.align-start {
    justify-content: flex-start;
    gap: 40px;
  }
}

.option-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.option-image {
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 2px;

  img {
    width: 36px;
    height: 36px;
    object-fit: contain;
    display: block;
  }
}

.option-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.option-label {
  font-family: 'GaleanaCondensed-Italic', sans-serif;
  font-size: 18px;
  color: var(--option-text-color) !important;
  white-space: nowrap;
  line-height: 1.1;
  text-align: center;
}

.percentage-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: -2px;
}

.percentage-value {
  font-family: 'RigidSquareExtrabold_Italic', sans-serif;
  font-size: 25px;
  color: var(--percentage-text-color);
  line-height: 1;
}

.progress-bar-container {
  min-width: 80px;
  width: 80px;
  height: 20px;
  background-color: var(--progress-bar-bg);
  overflow: hidden;
  flex-shrink: 0;
}

.progress-bar-fill {
  height: 100%;
  background-color: var(--progress-bar-fill);
  transition: width 0.3s ease;
}
</style>
